import { Component, type ErrorInfo, type ReactNode } from 'react';
import { trackError } from '../analytics';

interface Props {
  children: ReactNode;
}

interface State {
  failed: boolean;
}

/**
 * Last line of defence for the storefront.
 *
 * React unmounts the whole tree when a render throws, so without this a single
 * bad value leaves the customer looking at a blank white page with no message
 * and no way out. They cannot tell it from the site being down.
 *
 * The copy is Spanish and English rather than the visitor's language on
 * purpose: this sits *outside* the i18n provider, so that a crash inside the
 * provider itself is still caught. Reaching for translations here would mean
 * depending on the thing that may have just broken.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // The console keeps the stack for anyone with devtools open; Matomo is how
    // we find out it happened at all on someone else's phone.
    console.error('[caffeina] la pantalla se rompió:', error, info.componentStack);
    trackError('render', error);
  }

  render(): ReactNode {
    if (!this.state.failed) return this.props.children;

    return (
      <div className="crash" role="alert">
        <div className="crash-box">
          <p className="crash-title">Algo se rompió de nuestro lado.</p>
          <p className="crash-body">
            Probá recargar la página. Si sigue pasando, escribinos a{' '}
            <a href="https://instagram.com/caffeina.ba" target="_blank" rel="noopener noreferrer">
              @caffeina.ba
            </a>{' '}
            o pasá por Bonpland 943.
          </p>
          <p className="crash-body crash-body--alt" lang="en">
            Something broke on our end. Try reloading the page.
          </p>
          <button
            type="button"
            className="btn btn--solid btn--hero"
            onClick={() => window.location.reload()}
          >
            Recargar / Reload
          </button>
        </div>
      </div>
    );
  }
}
